import { Handshake, Users, Sparkles, TrendingUp, ExternalLink, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PartnerForm } from "@/components/forms/PartnerForm";

const CLUBE_INDICA_URL = "https://indicou.link/kentro/";

export function ParceriaPage() {
  return (
    <>
      {/* Hero */}
      <section className="gradient-hero relative overflow-hidden pt-24 pb-12">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <Sparkles className="size-3.5 text-primary-glow" />
            Programa de Parcerias Kentro
          </div>
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            Cresça junto com a <span className="gradient-text">Kentro</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Seja parceiro Kentro e ofereça aos seus clientes a plataforma de atendimento
            omnichannel com IA que está transformando empresas brasileiras. Ou, se você já é
            cliente, indique e ganhe pelo <strong className="text-foreground">Clube Indica</strong>.
          </p>
        </div>
      </section>

      {/* Dois caminhos */}
      <section className="border-y border-white/5 bg-background py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Card Cliente / Clube Indica */}
            <div className="glass-card rounded-2xl p-8">
              <div className="mb-4 inline-flex size-12 items-center justify-center rounded-xl bg-[var(--mint)]/15 text-[var(--mint)]">
                <Gift className="size-6" />
              </div>
              <h2 className="text-2xl font-bold">Já é cliente Kentro?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Indique outras empresas e ganhe recompensas em dinheiro pelo nosso programa
                oficial de indicações: o <strong>Clube Indica Kentro</strong>.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-[var(--mint)]" />
                  <span>Recompensas em dinheiro a cada indicação convertida</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-[var(--mint)]" />
                  <span>Acompanhamento de status das suas indicações em tempo real</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-[var(--mint)]" />
                  <span>Sem limite de indicações nem de ganhos</span>
                </li>
              </ul>
              <Button
                asChild
                size="lg"
                className="mt-8 w-full bg-[var(--mint)] text-background hover:opacity-90"
              >
                <a href={CLUBE_INDICA_URL} target="_blank" rel="noopener noreferrer">
                  Entrar no Clube Indica <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>

            {/* Card Parceiro */}
            <div className="glass-card rounded-2xl p-8 ring-1 ring-primary-glow/20">
              <div className="mb-4 inline-flex size-12 items-center justify-center rounded-xl bg-primary/15 text-primary-glow">
                <Handshake className="size-6" />
              </div>
              <h2 className="text-2xl font-bold">Quer ser parceiro Kentro?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Para agências, integradores, consultores e revendas que queiram entregar
                atendimento de classe mundial aos seus clientes.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary-glow" />
                  <span>Comissionamento recorrente e atrativo</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary-glow" />
                  <span>Treinamento técnico e comercial pela Kentro Academy</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary-glow" />
                  <span>Suporte dedicado e materiais de co-marketing</span>
                </li>
              </ul>
              <Button asChild size="lg" className="mt-8 w-full gradient-primary text-white shadow-glow">
                <a href="#candidatar">
                  <Handshake className="size-4" /> Quero ser parceiro
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios do programa */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold">Por que ser parceiro Kentro?</h2>
            <p className="mt-3 text-muted-foreground">
              Mais de 20 anos de mercado, milhares de empresas atendidas e uma plataforma
              construída para escalar com você.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Benefit
              icon={<TrendingUp className="size-5" />}
              title="Receita recorrente"
              text="Ganhe comissão mês após mês enquanto o cliente estiver ativo na plataforma."
            />
            <Benefit
              icon={<Users className="size-5" />}
              title="Time de apoio dedicado"
              text="Pré-vendas, sucesso do cliente e técnico, lado a lado com o seu time."
            />
            <Benefit
              icon={<Sparkles className="size-5" />}
              title="Produto com IA nativa"
              text="Apresente aos seus clientes IA aplicada a atendimento, vendas e canal de denúncias."
            />
          </div>
        </div>
      </section>

      {/* Formulário */}
      <section id="candidatar" className="border-t border-white/5 bg-background py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow">
              Candidatura
            </p>
            <h2 className="mt-2 text-3xl font-bold">Conte para a gente sobre você</h2>
            <p className="mt-3 text-muted-foreground">
              Preencha o formulário e nosso time de parcerias entra em contato em até 2 dias úteis.
            </p>
          </div>
          <div className="mt-10 glass-card rounded-2xl p-6 sm:p-8">
            <PartnerForm />
          </div>
        </div>
      </section>
    </>
  );
}

function Benefit({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="inline-flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary-glow">
        {icon}
      </div>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
